import { GQL_ID } from '@@types'
import { useCallback } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import { DEBOUNCE_TIMEOUT_UPDATE_AMOUNT } from '@shared/contants/product'

import { useMutateDropCartItem } from './useMutateDropCartItem'
import { useMutateUpdateCartProductAmount } from './useMutateUpdateCartProductAmount'
import { useCartStore } from '../model/useCartStore'

/**
 * Централизованный хук для управления изменениями корзины
 * Обеспечивает единый debounce для всех компонентов работы с корзиной
 */
export function useCartActions () {
  const {
    addOrUpdateProduct,
    removeProduct,
    currentShopId,
    syncCart,
    getProduct,
    setPendingRemoval,
    removePendingRemoval,
    hasPendingRemoval: hasPendingRemovalStore,
    getPendingRemoval,
    setRemovingProduct,
    clearRemovingProduct,
  } = useCartStore()
  const [ updateCartMutation ] = useMutateUpdateCartProductAmount()
  const [ dropCartMutation ] = useMutateDropCartItem()

  /**
   * Debounced обновление количества товара на сервере
   */
  const debouncedUpdateCart = useDebounceCallback(
    async (productId: GQL_ID, amount: number) => {
      // Удаляем из pending removals и устанавливаем состояние "удаляется"
      removePendingRemoval(productId)
      
      if (amount === 0) {
        setRemovingProduct(productId)
      }

      if (!currentShopId) {
        clearRemovingProduct(productId)
        return
      }

      try {
        if (amount === 0) {
          await dropCartMutation({
            variables: {
              shopId: currentShopId,
              productId,
            },
          })
        }
        else {
          await updateCartMutation({
            variables: {
              shopId: currentShopId,
              itemData: {
                product_id: productId,
                amount,
              },
            },
          })
        }
      }
      catch (error) {
        console.error('Error updating cart:', error)
        throw error
      }
      finally {
        clearRemovingProduct(productId)
      }
    },
    DEBOUNCE_TIMEOUT_UPDATE_AMOUNT
  )

  /**
   * Обновление количества товара с debounce
   * Если amount = 0, товар будет удален
   */
  const updateProductAmount = useCallback(
    (productId: GQL_ID, amount: number, isAuthorized: boolean) => {
      const currentProduct = getProduct(productId)
      const previousAmount = currentProduct?.amount || 0

      // Немедленно обновляем локальное состояние
      if (amount > 0) {
        addOrUpdateProduct({
          product_id: productId,
          amount,
        })
      }
      else {
        removeProduct(productId)
        
        // Добавляем в pending removals для кнопки "Вернуть"
        setPendingRemoval(productId, previousAmount)
      }

      // Если не авторизован - только локальное обновление
      if (!isAuthorized) {
        return
      }

      // Запускаем debounced обновление на сервере
      debouncedUpdateCart(productId, amount)
    },
    [ addOrUpdateProduct, removeProduct, getProduct, debouncedUpdateCart, setPendingRemoval ]
  )

  /**
   * Немедленное удаление товара (без debounce)
   * Используется для кнопок удаления
   */
  const removeProductImmediately = useCallback(
    async (productId: GQL_ID, isAuthorized: boolean) => {
      // Отменяем pending debounce
      debouncedUpdateCart.cancel()
      
      // Удаляем из pending removals
      removePendingRemoval(productId)

      // Удаляем локально
      removeProduct(productId)

      // Если авторизован - синхронизируем с сервером
      if (isAuthorized) {
        try {
          // Сначала сбрасываем все pending изменения
          await syncCart()
          
          // Затем удаляем товар
          await dropCartMutation({
            variables: {
              shopId: currentShopId as string,
              productId,
            },
          })
        }
        catch (error) {
          console.error('Error removing product:', error)
          throw error
        }
      }
    },
    [ removeProduct, currentShopId, syncCart, dropCartMutation, debouncedUpdateCart, removePendingRemoval ]
  )
  /**
   * Отмена pending удаления товара и восстановление предыдущего количества
   */
  const cancelPendingRemoval = useCallback(
    (productId: GQL_ID) => {
      const pendingRemoval = getPendingRemoval(productId)
      
      if (pendingRemoval) {
        // Отменяем debounced удаление
        debouncedUpdateCart.cancel()
        
        // Удаляем из pending
        removePendingRemoval(productId)
        
        // Восстанавливаем предыдущее количество
        addOrUpdateProduct({
          product_id: productId,
          amount: pendingRemoval.previousAmount,
        })
      }
    },
    [ getPendingRemoval, addOrUpdateProduct, debouncedUpdateCart, removePendingRemoval ]
  )

  /**
   * Проверка, есть ли pending удаление для товара
   */
  const hasPendingRemoval = useCallback(
    (productId: GQL_ID) => {
      return hasPendingRemovalStore(productId)
    },
    [ hasPendingRemovalStore ]
  )

  /**
   * Отмена всех pending обновлений
   * Полезно при размонтировании компонента или logout
   */
  const cancelPendingUpdates = useCallback(() => {
    debouncedUpdateCart.cancel()
  }, [ debouncedUpdateCart ])

  return {
    updateProductAmount,
    removeProductImmediately,
    cancelPendingRemoval,
    hasPendingRemoval,
    cancelPendingUpdates,
  }
}
